import { useEffect, useContext, useMemo } from "react";

import debounce from "lodash/debounce";
import { useSelector } from "react-redux";

import { UserContext } from "App/contexts";
import { useServerSentEvent } from "Hooks/useServerSentEvent";
import { getLogger } from "Services/LoggingService";

import { selectBrandAdminSettings, selectBrandAdminSetting } from "./selectors";

import type { AdminSetting } from "Types/Brand";

const logger = getLogger("Admin settings");

type ResolveSettingOptions = {
    name: string;
    setting: AdminSetting;
    variant?: string;
};

const resolveSettingValue = ({ setting, variant }: ResolveSettingOptions): Record<string, unknown> => {
    const { defaults, overrides } = setting;
    let resolved: Record<string, unknown>;
    if (!variant || !overrides[variant]) {
        resolved = defaults;
    } else {
        const override = overrides[variant];
        resolved = {
            ...defaults,
            ...override,
        };
    }

    return resolved;
};

export type UseBrandAdminSettingOptions = {
    name: string;
    variant?: string;
};

export const useBrandAdminSetting = ({ name, variant }: UseBrandAdminSettingOptions): Record<string, unknown> => {
    const setting = useSelector(selectBrandAdminSetting)(name);
    if (!setting) {
        logger.warn(`Setting ${name} not found`);
        return {};
    }

    return resolveSettingValue({ name, setting, variant });
};

let globalAdminSettings: Record<string, AdminSetting> = {};

/**
 * This is a component and not a hook so it can access the user context and can be implemented
 * on a top level container.
 */
export const BrandAdminSettingsLoader = () => {
    const settings = useSelector(selectBrandAdminSettings);
    useEffect(() => {
        globalAdminSettings = settings;
    }, [settings]);
    const { reloadBrand } = useContext(UserContext);
    const debouncedReloadBrand = useMemo(() => debounce(reloadBrand, 1000, { leading: true }), [reloadBrand]);

    useServerSentEvent("sse", "brand.adminSettingsUpdated", debouncedReloadBrand);

    return null;
};

export const getBrandAdminSetting = ({ name, variant }: UseBrandAdminSettingOptions): Record<string, unknown> => {
    if (!globalAdminSettings[name]) {
        logger.warn(`Setting ${name} not found`);
        return {};
    }

    return resolveSettingValue({ name, setting: globalAdminSettings[name], variant });
};
